import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { missionsApi } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import { ArrowLeft } from 'lucide-react';

const missionSchema = z.object({
  name: z.string().min(3, 'Mission name must be at least 3 characters'),
  location: z.string().min(3, 'Location is required'),
  flight_date: z.string().min(1, 'Flight date is required'),
  crop_type: z.string().optional(),
  area_hectares: z.number().min(0.1, 'Area must be at least 0.1 hectares').optional(),
  description: z.string().optional(),
});

type MissionFormData = z.infer<typeof missionSchema>;

export default function CreateMissionPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MissionFormData>({
    resolver: zodResolver(missionSchema),
  });

  const createMutation = useMutation({
    mutationFn: (data: MissionFormData) => missionsApi.create(data),
    onSuccess: (response) => {
      navigate(`/missions/${response.data.mission.id}`);
    },
    onError: (err: any) => {
      setError(
        err.response?.data?.message || 'Failed to create mission. Please try again.'
      );
    },
  });

  const onSubmit = (data: MissionFormData) => {
    setError('');
    createMutation.mutate(data);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate('/missions')}
          className="flex-shrink-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors mt-0.5"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 dark:text-white break-words">
            Create New Mission
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Plan a new drone survey mission
          </p>
        </div>
      </div>

      {error && (
        <Alert type="error" title="Error">
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Mission Name"
              placeholder="e.g., Tea Garden Survey - North Block"
              error={errors.name?.message}
              required
              {...register('name')}
            />

            <Input
              label="Location"
              placeholder="e.g., Assam, India"
              error={errors.location?.message}
              required
              {...register('location')}
            />

            <Input
              label="Flight Date"
              type="date"
              error={errors.flight_date?.message}
              required
              {...register('flight_date')}
            />

            <Input
              label="Crop Type"
              placeholder="e.g., Tea, Rice, Wheat"
              error={errors.crop_type?.message}
              {...register('crop_type')}
            />

            <Input
              label="Area (Hectares)"
              type="number"
              step="0.1"
              placeholder="e.g., 10.5"
              error={errors.area_hectares?.message}
              {...register('area_hectares', { valueAsNumber: true })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              rows={4}
              placeholder="Add any additional details about this mission..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              {...register('description')}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="flex gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/missions')}
            >
              Cancel
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              Create Mission
            </Button>
          </div>
        </form>
      </Card>

      {/* Info Card */}
      <Card className="bg-primary-50 border border-primary-200">
        <h3 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">
          📋 Next Steps
        </h3>
        <ul className="text-sm text-primary-800 dark:text-primary-200 space-y-1">
          <li>• After creating the mission, you can upload drone images</li>
          <li>• AI analysis will automatically process your images</li>
          <li>• View insights and recommendations in the analysis section</li>
        </ul>
      </Card>
    </div>
  );
}

// Made with Bob
